import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  useCallback,
  useMemo, useRef,
} from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useSessionContext } from '../../authentication/session';
import {
  useJWTResponse, loginRequest,
} from '../../authentication';

const apiPath = `${process.env.REACT_APP_ORIGINATION_API_PATH}/`;

const apiUrlDefault = (path: string) => `${apiPath}${path}`;

const apiConfig = {
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MILLISECONDS),
};

export const defaultCallApi = async (responseCall: any, apiCall: any) => {
  try {
    const response = (await apiCall).data;
    return response?.token ? responseCall(response) : response;
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error('defaultCallApi', e);
    throw e;
  }
};

export const useApi = (
  apiUrl: (path: string) => string = apiUrlDefault,
  errorHandler: ((res: AxiosError) => void) | null = null,
  callApi: (responseCall: any, apiCall: any) => Promise<any> = defaultCallApi,
) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const JWTResponse = useJWTResponse();
  const { setSessionExpTime } = useSessionContext();

  const authRef = useRef({
    instance, accounts, isAuthenticated, setSessionExpTime, errorHandler,
  });

  authRef.current = {
    instance, accounts, isAuthenticated, setSessionExpTime, errorHandler,
  };

  const api = useMemo<AxiosInstance>(() => {
    const inst = axios.create({ withCredentials: true, ...{ apiConfig } });

    inst.interceptors.request.use(async (config) => {
      const {
        // eslint-disable-next-line no-shadow
        instance, accounts, isAuthenticated, setSessionExpTime,
      } = authRef.current;

      const newConfig = { ...config };

      if (isAuthenticated && accounts.length > 0) {
        const tokenRequest = { ...loginRequest, account: accounts[0] };

        try {
          const { accessToken } = await instance.acquireTokenSilent(tokenRequest);
          setSessionExpTime(accessToken);
          newConfig.headers = {
            ...newConfig.headers,
            Authorization: `Bearer ${accessToken}`,
          };
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect(tokenRequest);
          }
          throw e;
        }
      }

      return newConfig;
    });

    inst.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const {
          // eslint-disable-next-line no-shadow
          errorHandler,
        } = authRef.current;
        errorHandler?.(error);
        throw error;
      },
    );

    return inst;
  }, []);

  const get = useCallback(
    (path: string) => callApi(JWTResponse, api.get(apiUrl(path))),
    [JWTResponse, api, apiUrl, callApi],
  );

  const post = useCallback(
    (path: string, data: any) => callApi(JWTResponse, api.post(apiUrl(path), data)),
    [JWTResponse, api, apiUrl, callApi],
  );

  const put = useCallback(
    (path: string, data: any) => callApi(JWTResponse, api.put(apiUrl(path), data)),
    [JWTResponse, api, apiUrl, callApi],
  );

  const del = useCallback(
    (path: string, data: any = {}) => callApi(JWTResponse, api.delete(apiUrl(path), { data })),
    [JWTResponse, api, apiUrl, callApi],
  );

  return {
    get, post, put, del,
  };
};
